import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import { PalletDto, PalletEditDto } from 'src/store/pallets/pallet';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum PalletTypes {
  FetchAll = 'pallets/FetchAll',
  Add = 'pallets/Add',
  Update = 'pallets/Update',
  Delete = 'pallets/Delete',
  Failure = 'pallets/Failure'
}

export type PalletAction =
  | { type: PalletTypes.FetchAll; pallets: PalletDto[] }
  | { type: PalletTypes.Add; pallet: PalletDto }
  | { type: PalletTypes.Update; pallet: PalletDto }
  | { type: PalletTypes.Delete; pallet: PalletDto }
  | { type: PalletTypes.Failure; errors: any };

export const actionCreators = {
  fetchPallets: (pallets: PalletDto[]): PalletAction => ({
    type: PalletTypes.FetchAll,
    pallets
  }),
  addPallet: (pallet: PalletDto): PalletAction => ({
    type: PalletTypes.Add,
    pallet
  }),
  updatePallet: (pallet: PalletDto): PalletAction => ({
    type: PalletTypes.Update,
    pallet
  }),
  deletePallet: (pallet: PalletDto): PalletAction => ({
    type: PalletTypes.Delete,
    pallet
  }),
  palletRequestFailure: (errors: any): PalletAction => ({
    type: PalletTypes.Failure,
    errors
  })
};

export const fetchPallets = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(PalletTypes.FetchAll));
    const { ok, data, error } = await getRequest<PalletDto[]>('/pallets');
    dispatch(requestEnd(PalletTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchPallets(data))
      : dispatch(actionCreators.palletRequestFailure(error));
  };
};

export const createPallet = (pallet: PalletEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>('/pallets', pallet);

    if (ok) {
      dispatch(alertSuccess(i18n.t('pallet.created')));
      return dispatch(actionCreators.addPallet({ ...pallet, id: data }));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.palletRequestFailure(error));
  };
};

export const editPallet = (id: number, pallet: PalletEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/pallets/${id}`, pallet);

    if (ok) {
      dispatch(alertSuccess(i18n.t('pallet.updated')));
      return dispatch(actionCreators.updatePallet(pallet as PalletDto));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.palletRequestFailure(error));
  };
};

export const deletePallet = (id: number, pallet: PalletDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/pallets/${id}`, pallet);

    if (ok) {
      dispatch(alertSuccess(i18n.t('pallet.deleted')));
      return dispatch(actionCreators.deletePallet(pallet));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.palletRequestFailure(error));
  };
};
